import { type MouseEvent, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { GA4EventName, GA4FeatureCategory } from '@jsmdg/tracking';
import { PureButton, PureLink } from '@jsmdg/yoshi';
import { type NavigationItem } from '../../../shared/types';
import { getLinkWithFiltersParams } from '../../helper/getLinkWithLocationParams';
import { isInternalLink } from '../../helper/isInternalLink';
import {
    availableTrackingActions,
    trackingCategories,
    trackNavigationClick,
} from '../../helper/trackNavigation';
import { useActivateListItemWithDelay } from '../../hooks/useActivateListItemWithDelay';
import { useHoverTracking } from '../../hooks/useHoverTracking';
import { DesktopNavigationGroup } from './DesktopNavigationGroup/DesktopNavigationGroup';
import styles from './DesktopNavigation.module.scss';

const messages = defineMessages({
    mainNavigation: {
        defaultMessage: 'Hauptnavigation',
    },
});

function isCategoryLink(category: NavigationItem): boolean {
    return Boolean(category.uri && !category?.children?.length);
}

const onLinkClick = (e: MouseEvent<HTMLAnchorElement>, uri: string): void => {
    e.preventDefault();
    window.location.href = getLinkWithFiltersParams(uri);
};

type DesktopNavigationProps = {
    readonly toggleBackdrop: (showBackdrop: boolean) => void;
};

const DesktopNavigation = ({ toggleBackdrop }: DesktopNavigationProps): JSX.Element => {
    const currentNavigationDepth = 0;
    const maxNavigationDepth = 2;
    const { setIsFromTouchEvent, topNavigationItems, trackDesktopNavigationHoverIn } =
        useHoverTracking(currentNavigationDepth, maxNavigationDepth);

    const { activeItemIndex, resetActiveItemIndexWithDelay, setActiveItemIndexWithDelay } =
        useActivateListItemWithDelay();

    const intl = useIntl();

    useEffect(() => {
        toggleBackdrop(
            Boolean(activeItemIndex > -1 && topNavigationItems[activeItemIndex]?.children?.length),
        );
    }, [activeItemIndex, toggleBackdrop, topNavigationItems]);

    return (
        <nav aria-label={intl.formatMessage(messages.mainNavigation)} className="d-none d-md-flex">
            <ul className={classNames(styles.desktopNav, 'p-0 d-flex w-100')}>
                {topNavigationItems.map((item: NavigationItem, index: number) => {
                    const navigationItemHasChildren = item?.children?.some(
                        child => child?.children && child.children.length > 0,
                    );

                    const trackingData = {
                        eventName: GA4EventName.OpenNavigationItem,
                        feature_category: GA4FeatureCategory.TopNavigation,
                        click_element: item.title as string,
                        click_text: item.title,
                        navigationlevel: 0,
                    };

                    return (
                        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
                        <li
                            className={classNames(styles.navCategory, 'py-1x', {
                                [styles.open]: activeItemIndex === index,
                                [styles.highlighted]: item.isHighlighted,
                            })}
                            key={item.id}
                            onTouchStart={() => setIsFromTouchEvent(true)}
                            onMouseLeave={() => {
                                resetActiveItemIndexWithDelay();
                            }}
                            onMouseEnter={() => {
                                setActiveItemIndexWithDelay(index);
                                trackDesktopNavigationHoverIn(
                                    item,
                                    item.title as string,
                                    trackingData,
                                );
                            }}
                            onFocus={() => {
                                setActiveItemIndexWithDelay(index);
                            }}
                            onBlur={() => {
                                resetActiveItemIndexWithDelay();
                            }}
                            onClick={e =>
                                trackNavigationClick({
                                    e,
                                    category: trackingCategories.TOP_NAVI,
                                    action: availableTrackingActions.CLICK,
                                    eventData: {
                                        ...trackingData,
                                        eventName: GA4EventName.ClickNavigationItem,
                                        click_url: window.location.origin + item.uri,
                                    },
                                })
                            }
                        >
                            {isCategoryLink(item) ? (
                                // eslint-disable-next-line no-warning-comments
                                // TODO: check for adjustment in navigation backend JSON to reinstate Magazin
                                <PureLink
                                    href={item.uri}
                                    className={classNames(
                                        'font-complementary',
                                        styles.categoryLink,
                                    )}
                                    // remove spreaded props when rendition of judgement on AT may be removed
                                    // eslint-disable-next-line react/jsx-props-no-spreading
                                    {...(item.id === 'urteilsveroeffentlichung' && {
                                        target: '_blank',
                                    })}
                                    internal={isInternalLink(item.uri)}
                                    onClick={e => onLinkClick(e, item.uri)}
                                >
                                    {item.title}
                                </PureLink>
                            ) : (
                                <>
                                    <PureButton
                                        className={classNames(
                                            'font-complementary position-relative',
                                            styles.categoryTitle,
                                        )}
                                    >
                                        {item.title}
                                    </PureButton>
                                    {item.children && (
                                        <div
                                            className={classNames(
                                                'mt-1x position-absolute',
                                                styles.searchFlyoutWrapper,
                                                {
                                                    [styles.flyoutWrapperWithMultiColumn]:
                                                        navigationItemHasChildren,
                                                },
                                            )}
                                        >
                                            <DesktopNavigationGroup
                                                className={
                                                    navigationItemHasChildren ? 'w-50' : 'w-100'
                                                }
                                                group={item}
                                                depth={currentNavigationDepth}
                                                maxDepth={maxNavigationDepth}
                                                parentTrackingLabel={item.title as string}
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </li>
                    );
                })}
            </ul>
        </nav>
    );
};

export { DesktopNavigation };
